import errorHandler from 'Utils/errorHandler.js';
import whenAvailable from 'Utils/whenAvailable.js';

const gartnerOpenListeners = new WeakMap();
const gartnerOpenHandler = /^window\.open\("([^"]+)", "_blank"\)$/;
const gartnerStopPropagation = /(^|\s)onclick="event\.stopPropagation\(\);"/g;

/*
 * The Peer Insights widget writes inline click handlers. script-src-attr blocks
 * them, so the badge never opens and the block is reported (via Osano, which
 * wraps innerHTML and setAttribute). Convert the handlers it writes into
 * listeners before they reach the DOM.
 */
function bridgeGartnerInlineClicks() {
	const originalSetAttribute = Element.prototype.setAttribute;

	Element.prototype.setAttribute = function (name, value) {
		const openUrl = gartnerOpenUrl(this, name, value);
		if (openUrl) {
			bindGartnerOpen(this, openUrl);
			return;
		}

		return originalSetAttribute.call(this, name, value);
	};

	const prototype = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML')?.set
		? Element.prototype
		: HTMLElement.prototype;
	const innerHtml = Object.getOwnPropertyDescriptor(prototype, 'innerHTML');

	Object.defineProperty(prototype, 'innerHTML', {
		configurable: innerHtml.configurable,
		enumerable: innerHtml.enumerable,
		get() {
			return innerHtml.get.call(this);
		},
		set(value) {
			const html = withoutGartnerStopPropagation(value);
			innerHtml.set.call(this, html);
			if (html === value) {
				return;
			}

			for (const anchor of this.querySelectorAll('[data-gs-stop-click]')) {
				anchor.addEventListener('click', event => {
					event.stopPropagation();
				});
				anchor.removeAttribute('data-gs-stop-click');
			}
		},
	});
}

/* The badge link is a div whose click target is only present in the inline handler. */
function gartnerOpenUrl(element, name, value) {
	if (!element?.id?.startsWith('gartner-pi-link')) {
		return '';
	}

	if (String(name).toLowerCase() !== 'onclick' || typeof value !== 'string') {
		return '';
	}

	return gartnerOpenHandler.exec(value)?.[1] ?? '';
}

function bindGartnerOpen(element, url) {
	const previous = gartnerOpenListeners.get(element);
	if (previous) {
		element.removeEventListener('click', previous);
	}

	const openBadge = () => {
		window.open(url, '_blank');
	};

	gartnerOpenListeners.set(element, openBadge);
	element.addEventListener('click', openBadge);
}

/* Links inside the badge stop the click from also opening the badge URL. */
function withoutGartnerStopPropagation(value) {
	if (typeof value !== 'string' || !value.includes('gartner-pi-')) {
		return value;
	}

	gartnerStopPropagation.lastIndex = 0;
	return value.replace(gartnerStopPropagation, '$1data-gs-stop-click=""');
}

document.addEventListener('DOMContentLoaded', () => {
	try {
		bridgeGartnerInlineClicks();

		/* Load the Gartner widget */
		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.crossOrigin = 'anonymous';
		script.src = 'https://www.gartner.com/peer-insights/vendor-portal/public/Widget/js/widget.js';

		/* Initialise once available */
		script.addEventListener('load', () => {
			whenAvailable('GartnerPI_Widget')
				.then(() => {
					/* eslint-disable-next-line new-cap */
					window.GartnerPI_Widget({
						size: 'line',
						theme: 'light',
						sourcingLink: '',
						widget_id: 'YzkzZmIyZWEtNjkwMC00YTNiLWIyOGMtZmY3NDBjZDIxMmFj',
						version: '2',
						container: document.querySelector('#footer-gartner-badge'),
					});
				});
		});

		/* Add to document */
		document.head.append(script);
	} catch (error) {
		errorHandler(error);
	}
});
